import {Three as THREE, ThreeTsl as TSL} from "#material/three/exports"

class PhysicalLightingModel extends THREE.PhysicalLightingModel {
    override direct(input: THREE.LightingModelDirectInput, builder: THREE.NodeBuilder) {
        // Sphere and sun lights (SphereLightNode / SunLightNode) bring their own LTC diffuse + specular; add both
        // and skip super for them.
        const ltcLight = input as {ltcDiffuse?: THREE.Node<"vec3">; ltcSpecular?: THREE.Node<"vec3">}
        if (ltcLight.ltcDiffuse && ltcLight.ltcSpecular) {
            ;(input.reflectedLight.directDiffuse as THREE.Node<"vec3">).addAssign(ltcLight.ltcDiffuse)
            ;(input.reflectedLight.directSpecular as THREE.Node<"vec3">).addAssign(ltcLight.ltcSpecular)
            return
        }

        super.direct(input, builder)
    }

    override finish(builder: THREE.NodeBuilder) {
        const context = builder.getContext()

        const {outgoingLight, reflectedLight, material, backdrop} = context
        const {directDiffuse, directSpecular, indirectDiffuse, indirectSpecular} = reflectedLight as THREE.NodeBuilderContext

        // Energy conservation to match Cycles' layered diffuse-under-specular: attenuate the direct diffuse by
        // the specular's grazing Fresnel transmittance (1 - F(N·V)), so it dims at grazing as the specular
        // takes the energy. Roughness-aware f90 (Lagarde) so matte surfaces don't over-dim; direct term only
        // (the indirect/IBL path already compensates). Scaled here so it covers every light.
        if (directDiffuse instanceof THREE.Node) {
            const ndotv = TSL.normalView.dot(TSL.positionViewDirection).clamp()
            const f0 = TSL.property("color", "SpecularColor") as unknown as THREE.Node<"vec3">
            const diffuseFresnel = TSL.F_Schlick({f0, f90: TSL.roughness.oneMinus().clamp(), dotVH: ndotv}) as unknown as THREE.Node<"vec3">
            ;(directDiffuse as THREE.Node<"vec3">).mulAssign(diffuseFresnel.oneMinus())
        }

        if (material instanceof ThreeColormassMaterial) {
            const {uvShadowMapNode, uvShadowMapIntensityNode} = material

            if (uvShadowMapNode) {
                const uvShadowOcclusion = TSL.mix(
                    uvShadowMapNode.r,
                    TSL.float(1.0),
                    uvShadowMapIntensityNode ? TSL.sub(1.0, TSL.clamp(uvShadowMapIntensityNode, 0.0, 1.0)) : 0,
                )

                if (
                    outgoingLight instanceof THREE.Node &&
                    directDiffuse instanceof THREE.Node &&
                    directSpecular instanceof THREE.Node &&
                    indirectDiffuse instanceof THREE.Node &&
                    indirectSpecular instanceof THREE.Node
                ) {
                    if (backdrop !== null) throw new Error("Not implemented backdrop handling in ThreeColormassMaterial")

                    // instanceof narrows to Node<unknown>; cast to vec3 for TSL math operations
                    const dd = directDiffuse as THREE.Node<"vec3">
                    const ds = directSpecular as THREE.Node<"vec3">
                    const id = indirectDiffuse as THREE.Node<"vec3">
                    const is_ = indirectSpecular as THREE.Node<"vec3">
                    const ol = outgoingLight as THREE.Node<"vec3">

                    const totalDiffuse = dd.mul(uvShadowOcclusion).add(id)
                    const totalSpecular = ds.mul(uvShadowOcclusion).add(is_)

                    ol.assign(totalDiffuse.add(totalSpecular))
                }
            }
        }

        super.finish(builder)
    }
}

export class ThreeColormassMaterial extends THREE.MeshPhysicalNodeMaterial {
    uvShadowMapNode: THREE.TextureNode | null = null
    uvShadowMapIntensityNode: THREE.Node<"float"> | null = null

    override setupLightingModel() {
        const lightingModel = new PhysicalLightingModel(
            this.useClearcoat,
            this.useSheen,
            this.useIridescence,
            this.useAnisotropy,
            this.useTransmission,
            this.useDispersion,
        )

        return lightingModel
    }

    override copy(source: this): this {
        super.copy(source)
        this.iorNode = source.iorNode
        this.uvShadowMapNode = source.uvShadowMapNode
        this.uvShadowMapIntensityNode = source.uvShadowMapIntensityNode
        return this
    }

    override setupSpecular(): void {
        super.setupSpecular()

        //In Cycles, grazing reflection goes to zero for IOR -> 1
        //This is not the case in three.js, so we adjust the F90 term here
        //This makes materials look more similar between the two renderers
        const f0 = TSL.pow(TSL.div(TSL.sub(TSL.ior, 1.0), TSL.add(TSL.ior, 1.0)), 2.0)
        const f0Norm = TSL.clamp(TSL.div(f0, 0.04), 0.0, 1.0)
        TSL.specularF90.assign(f0Norm.pow(0.3))
    }
}
